import useLocalStorage from "@rehooks/local-storage";
import cx from "classnames";
import Skeleton from "react-loading-skeleton";
import { useNavigate } from "react-router-dom";
import { ApolloError } from "@apollo/client";

import { useCssTransition } from "hooks/useCssTransition";
import { TransitionState } from "hooks/useCssTransition/types";
import { ChevronRight, ConfettiColored, Tick, Rocket } from "components/icons/generated";
import Box from "ds/components/Box";
import Icon from "ds/components/Icon";
import TileContent from "ds/components/Tile/Content";
import TileTitle from "ds/components/Tile/Title";
import TileWrapper from "ds/components/Tile/Wrapper";
import Typography from "ds/components/Typography";
import EmptyState from "ds/components/EmptyState";
import IconEmphasized from "ds/components/IconEmphasized";
import TileIndicator from "ds/components/Tile/Indicator";
import { EXPLORE_NEXT_FEATURES } from "views/Account/LaunchPad/ExploreNext/constants";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageDashboard } from "hooks/useAnalytics/pages/dashboard";
import MissingDataBanner from "components/MissingDataBanner";
import { LaunchPadFeatureMetadata, LaunchPadsFeatureProgress } from "views/Account/LaunchPad/types";
import Card from "ds/components/Card";

import styles from "./styles.module.css";
import DashboardOverviewColumnOnboardingDropdown from "../OnboardingDropdown";

type DashboardOverviewColumnOnboardingLayoutProps<T extends string> = {
  onboardingProgressInitialLoading: boolean;
  progressByFeature: LaunchPadsFeatureProgress<T>;
  isOnboardingComplete: boolean;
  error: ApolloError | undefined;
  refetch: () => void;
  features: LaunchPadFeatureMetadata<T>[];
};

const DashboardOverviewColumnOnboardingLayout = <T extends string>({
  onboardingProgressInitialLoading,
  progressByFeature,
  isOnboardingComplete,
  error,
  refetch,
  features,
}: DashboardOverviewColumnOnboardingLayoutProps<T>) => {
  const [isOnboardingFinishedLocalStorage, setIsOnboardingFinishedLocalStorage] = useLocalStorage(
    "dashboardWidgetOnboardingFinished",
    false
  );

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageDashboard.Dashboard,
  });

  const navigate = useNavigate();

  const setOnboardingFinished = () => {
    if (!isOnboardingFinishedLocalStorage) {
      setIsOnboardingFinishedLocalStorage(true);
    }
  };

  const goToOnboardingFeature = (url: string, title: string) => {
    const itemsLength = features.length;
    const doneItemsLength = features.filter(
      ({ feature }) => progressByFeature[feature].isCompleted
    ).length;

    trackSegmentAnalyticsEvent("Dashboard - Onboarding Checklist Clicked", {
      step: title,
      completed: (doneItemsLength * 100) / itemsLength,
    });
    navigate(url);
  };

  const goToExploreNextFeature = (url: string, title: string) => {
    trackSegmentAnalyticsEvent("Dashboard - Explore Next Clicked", { step: title });
    navigate(url);
  };

  const { state } = useCssTransition({
    visible: !isOnboardingComplete,
    timeout: 3000,
    unmountOnExit: false,
    onExited: setOnboardingFinished,
  });

  if (error) {
    return (
      <Card
        title="Onboarding"
        headerPrefix={<Icon src={Rocket} />}
        headerSuffix={<DashboardOverviewColumnOnboardingDropdown />}
      >
        <MissingDataBanner
          text="Couldn’t load onboarding data. Please try to refresh or come back later. "
          refreshHandler={refetch}
        />
      </Card>
    );
  }

  return (
    <div className={styles.wrapper}>
      <Box direction="column" fullWidth>
        <Card
          contentGap="large"
          title="Continue your onboarding"
          className={cx(
            styles.onboarding,
            styles.onboardingContinue,
            (state === TransitionState.EXITED || isOnboardingFinishedLocalStorage) && styles.hidden
          )}
          headerPrefix={<Icon src={Rocket} />}
          headerSuffix={<DashboardOverviewColumnOnboardingDropdown />}
        >
          {onboardingProgressInitialLoading && (
            <Skeleton
              className={styles.onboardingSkeleton}
              count={4}
              height={39}
              width="100%"
              borderRadius={12}
            />
          )}

          {!onboardingProgressInitialLoading && isOnboardingComplete && (
            <Box
              align="center"
              gap="x-large"
              padding="x-large"
              className={cx(styles.onboardingItem, styles.onboardingEmptyItem)}
            >
              <EmptyState
                icon={ConfettiColored}
                title="Congratulations"
                caption="You have finished your onboarding setup!"
              />
            </Box>
          )}

          {!onboardingProgressInitialLoading &&
            !isOnboardingComplete &&
            !isOnboardingFinishedLocalStorage && (
              <Box direction="column" gap="medium">
                {features.map(({ feature, title, url }) => {
                  const isCompleted = progressByFeature[feature].isCompleted;

                  return (
                    <Box
                      align="center"
                      gap="x-large"
                      padding="medium large"
                      className={cx(styles.onboardingItem, isCompleted && styles.completed)}
                      key={feature}
                      onClick={!isCompleted ? () => goToOnboardingFeature(url, title) : undefined}
                    >
                      <div
                        className={cx(styles.onboardingItemIcon, isCompleted && styles.completed)}
                      >
                        <Icon color="on-inversed" src={Tick} />
                      </div>

                      <Typography tag="span" variant="p-t7">
                        {title}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
        </Card>

        <Card
          className={cx(
            styles.onboarding,
            state !== TransitionState.EXITED && !isOnboardingFinishedLocalStorage && styles.hidden
          )}
          contentGap="large"
          title="Explore next"
          headerPrefix={<Icon src={Rocket} />}
          headerSuffix={<DashboardOverviewColumnOnboardingDropdown />}
        >
          <Box direction="column" gap="small">
            {EXPLORE_NEXT_FEATURES.map((feature, i) => {
              return (
                <TileWrapper
                  key={i}
                  className={styles.exploreNextItem}
                  onClick={() => goToExploreNextFeature(feature.to, feature.title)}
                >
                  <Box justify="between" grow="1">
                    <TileContent direction="row" align="center" justify="between">
                      <Box align="center" gap="medium">
                        <IconEmphasized icon={feature.icon} className={feature.iconTileClassName} />
                        <TileTitle>{feature.title}</TileTitle>
                      </Box>
                      <TileIndicator indicator={<Icon src={ChevronRight} />} />
                    </TileContent>
                  </Box>
                </TileWrapper>
              );
            })}
          </Box>
        </Card>
      </Box>
    </div>
  );
};

export default DashboardOverviewColumnOnboardingLayout;
